import {createAsyncThunk, createSlice, isFulfilled, isPending, isRejected} from '@reduxjs/toolkit';
import axios from 'axios';
import {serializeAxiosError} from '../../../../Config/Reducers/ReducerUtils';
import {DashboardNavigationMenuState} from './types/DashboardNavigationMenuState';
import {RootState} from '../../../../Config/Store';
import {DashboardNavigationMenu} from './types/DashboardNavigationMenu';
import {Response} from '../../../../Config/Types';

const initialState: DashboardNavigationMenuState = {
    isLoading: false,
    errorMessage: null,
    entities: [],
    status: 'idle',
    activeGenerationId: null,
    activeRequestId: null,
};

const apiUrl = 'api/power-station/navigation-tree';

export type NavigationTreeRequest = {
    generationId: number;
};

export const getEntities = createAsyncThunk(
    'power-station/navigation-menu/entity_list',
    async ({ generationId }: NavigationTreeRequest) => {
        const response = await axios.get<Response<DashboardNavigationMenu>>(apiUrl, { timeout: 50000 });
        return { generationId, response };
    },
    { serializeError: serializeAxiosError },
);

const mapEntities = (action: { payload: { response: { data: { data: DashboardNavigationMenu[] } } } }) => {
    return action.payload.response.data.data.map(item => {
        const equipmentListWithPowerStation = item?.equipmentList.map(equipment => ({
            ...equipment,
        }));
        const skidListWithEquipments = item.skidList.map(skid => ({
            ...skid,
            equipmentList: skid.equipmentList.map(equipment => ({
                ...equipment,
            })),
        }));
        return {
            ...item,
            skidList: skidListWithEquipments,
            equipmentList: equipmentListWithPowerStation,
        };
    });
};

const dashboardNavigationMenuReducer = createSlice({
    name: 'dashboardNavigationMenuReducer',
    initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(isPending(getEntities), (state, action) => {
                const generationId = action.meta.arg.generationId;
                const requestId = action.meta.requestId;
                return {
                    ...state,
                    isLoading: true,
                    status: 'loading',
                    errorMessage: null,
                    activeGenerationId: generationId,
                    activeRequestId: requestId,
                };
            })
            .addMatcher(isFulfilled(getEntities), (state, action) => {
                const generationId = action.payload.generationId;
                const requestId = action.meta.requestId;
                if (
                    state.activeGenerationId !== generationId ||
                    state.activeRequestId !== requestId
                ) {
                    return state;
                }
                return {
                    ...state,
                    isLoading: false,
                    status: 'ready',
                    errorMessage: null,
                    entities: mapEntities(action),
                };
            })
            .addMatcher(isRejected(getEntities), (state, action) => {
                const generationId = action.meta.arg?.generationId;
                const requestId = action.meta.requestId;
                if (
                    generationId !== undefined &&
                    (state.activeGenerationId !== generationId || state.activeRequestId !== requestId)
                ) {
                    return state;
                }
                return {
                    ...state,
                    isLoading: false,
                    status: 'error',
                    errorMessage: action.error.message ?? 'navigation_tree_error',
                };
            });
    },
});

export const { reset } = dashboardNavigationMenuReducer.actions;
export const selectGDashboardNavigationMenu = (state: RootState) => state.dashboardNavigationMenu;
export default dashboardNavigationMenuReducer.reducer;
